<template>
  <div
    class="phone-schedule-board relative -mx-5 h-97dvh"
    :class="[
      { 'phone-columns': phoneColumnsEnabled },
      isWeekView ? 'phone-week-view' : 'phone-day-view'
    ]"
    :data-today-column="todayColumn"
  >
    <!-- With the flag on, SchedulePageV2 renders the control row in place of
         this stack. Held back until the flag has answered so the interactjs
         handles never mount only to be torn down. -->
    <MobileTimelineHeader
      v-if="flagsResolved && !mobileWebOptimization"
      :resources="resources"
      :active-resource="activeResource"
    />
    <!-- Two independent questions about the same element. The PADDING goes
         with the card stack, which the columns board replaces with its header
         band. The RULE is drawn by whatever sits above the board: with the flag
         off that is MobileTimelineHeader, so the board draws its own; with the
         flag on the page's date row butts against it and already carries a
         bottom border, and a second one here stacks into a 2px line. -->
    <div
      :class="[
        'relative h-full',
        { 'pt-4': !phoneColumnsEnabled, 'border-t': !mobileWebOptimization }
      ]"
    >
      <div ref="eventCalendarRef"></div>
    </div>
    <div class="fixed bottom-12 right-4 z-10 flex gap-3">
      <!-- Unassigned work is a column on the grid once the columns render, so
           the badge that opened it in a modal would be a second, stale way in. -->
      <UnassignedEventsFOB v-if="!phoneColumnsEnabled" />
      <CreateNewFOB
        :default-date="date"
        :sheet="isSheet"
        :flags-resolved="flagsResolved"
      />
    </div>
    <div>
      <TimelineFooter :last-load="lastLoad" />
    </div>
    <AppointmentDetailsDrawer
      v-if="selectedAppointmentId"
      :appointment-id="selectedAppointmentId"
      :placement="placement"
      @hide-drawer="onHideDrawer"
      @appointment-updated="onAppointmentUpdated"
    />
    <AppointmentCreateModal
      v-if="createSlot && isSheet"
      sheet
      :initial_date="createSlot.date"
      :scheduled_time="createSlot.scheduledTime"
      :technician_id="createSlot.technicianUserId"
      @hide-modal="clearCreateSlot"
      @created="onCreated"
    />
    <!-- Held back until the flag has actually answered: the two surfaces differ
         by more than a class (the desktop one is the jQuery modal, which shows
         itself and registers a backdrop on mount), so mounting the wrong one
         first and swapping would strand that backdrop. -->
    <CalendarEventModal
      v-if="selectedEventId && flagsResolved"
      :calendar-event-id="selectedEventId || ''"
      :sheet="isSheet"
      auto-open
      @close="closeEventModal"
    ></CalendarEventModal>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import moment from "moment-timezone";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { originStack, reportRumError } from "@/lib/datadog";
import { getComponentHtml } from "@/lib/getComponentHTML";
import { getQueryVariables } from "@/lib/url";
import { useResolvedFeatureFlag } from "@/lib/useFeatureFlag";

import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import AppointmentDetailsDrawer from "@/AppointmentDetails/AppointmentDetailsDrawer.vue";
import { useSheetPlacement } from "@/composables/useSheetPlacement";
import CalendarEventModal from "@/Scheduler/CalendarEventModal.vue";
import { useEventCalendar } from "@/SchedulerV2/useEventCalendar";
import { AppointmentV2 } from "@/typings/appointment";
import {
  MbscCalendarEventData,
  MbscCellClickEvent,
  MbscResource
} from "@mobiscroll/javascript";
import AppointmentEvent from "./AppointmentEvent.vue";
import CreateNewFOB from "./CreateNewFOB.vue";
import MobileAppointmentEvent from "./MobileAppointmentEvent.vue";
import MobileTimelineHeader from "./MobileTimelineHeader.vue";
import ScheduleColumnHeader from "./ScheduleColumnHeader.vue";
import TimelineFooter from "./TimelineFooter.vue";
import UnassignedEventsFOB from "./UnassignedEventsFOB.vue";
import WeekDayHeader from "./WeekDayHeader.vue";

const {
  isWeekView,
  phoneColumnsEnabled,
  applyDragGate,
  clearCreateSlot,
  dragOptionsFor,
  createSlot,
  handleCellClick,
  initCalendar,
  destroyCalendar,
  setCalendarDate,
  reRenderResources,
  resources,
  activeResource,
  selectedAppointmentId,
  selectedEventId,
  setTimezoneLabel,
  handleEventRightClick,
  handleEventClick
} = useEventCalendar();
const { isSheet, placement } = useSheetPlacement();
// The card stack is the flag's disabled branch, so it waits for the resolved
// answer rather than for `enabled`; the event surface below shares the gate.
const { enabled: mobileWebOptimization, resolved: flagsResolved } =
  useResolvedFeatureFlag("mobile_web_optimization");
const props = defineProps<{ date: Date }>();
const emit = defineEmits<{
  (e: "date-navigated", date: Date | string): void;
}>();
const eventCalendarRef = ref<HTMLElement | null>(null);
const lastLoad = ref(new Date());

/**
 * Which column the tint goes behind, as Mobiscroll's own weekday class suffix,
 * or null when there is nothing to tint. Null in day view, and null in any week
 * that is not this one -- otherwise navigating to next week would still shade
 * whichever column happens to share today's weekday.
 */
const todayColumn = computed(() => {
  if (!isWeekView.value) return null;
  if (!moment(props.date).isSame(moment(), "isoWeek")) return null;
  return moment().format("ddd").toLowerCase();
});

// The gate is the flag AND the phone layout, and the phone half is already
// structural here: SchedulePageV2.vue mounts this component only under
// useBreakpoints().type === "mobile", so inside this file "flag on" and
// "flag on and phone layout" are the same condition. A second
// useMobileWebOptimization() call would install a second fetchingTenant
// watcher and a second conditional fetchTenant dispatch in one component for
// no behavioural gain -- reuse the useResolvedFeatureFlag pair this file
// already declares instead.
//
// renderEventHtml reads the flag at CALL time, not once at initCalendar
// construction, because the tenant fetch can settle after mount. The
// flag-off branch deliberately calls getComponentHtml with two arguments and
// is NOT wrapped in the try/catch below, exactly as it does today: AC-10
// requires that path to render byte-identical output, and today a desktop-
// card render throw is swallowed by Vue rather than contained -- that is a
// decision, not an oversight. Scoping the try/catch to the flag-on branch
// only is what keeps that decision true regardless of build (dev, Vitest,
// Playwright's dev server all let Vue rethrow, unlike production).
const renderEventHtml = (data: MbscCalendarEventData): string => {
  if (!mobileWebOptimization.value) {
    return getComponentHtml(AppointmentEvent, { event: data });
  }
  try {
    return getComponentHtml(
      MobileAppointmentEvent,
      { event: data, variant: isWeekView.value ? "week" : "day" },
      { rethrowRenderErrors: true }
    );
  } catch (err) {
    // origin_stack is what makes this report actionable: without it every
    // one of these arrives stamped with reportRumError's own stack rather
    // than the throw site (ENG-591's ~40 throws/hour that could not be
    // placed), per the sanctioned precedent in lib/setupVue.ts.
    const origin_stack = originStack(err);
    reportRumError("vue.render_error", {
      component: "MobileAppointmentEvent",
      phase: "renderScheduleEvent",
      view_path: window.location?.pathname,
      message: err instanceof Error ? err.message : String(err),
      ...(origin_stack ? { origin_stack } : {})
    });
    // A constant fallback -- nothing from the event payload is interpolated
    // into this string, since it is the one place on this path a raw
    // concatenation could carry server data unescaped into the DOM.
    return '<div class="mbl-card-fallback" data-cy="mobile-card-fallback"></div>';
  }
};

// Held back until the flag has resolved: renderEventHtml reads the flag at
// call time, but Mobiscroll only renders when data is set and then re-renders
// on its own poll, so a board whose events settle before the tenant fetch
// does paints the old card and keeps it painted for up to that poll interval
// -- visible to a real dispatcher as a card that changes shape under them.
// The guard makes startCalendar run exactly once either way.
const calendarStarted = ref(false);
const startCalendar = () => {
  if (calendarStarted.value) return;
  calendarStarted.value = true;
  initCalendar(
    eventCalendarRef.value,
    props.date,
    {
      renderScheduleEvent: renderEventHtml,
      // Blank while the board draws a single schedule: there is only one column
      // and the control row above it already names whose it is.
      renderResource: (data: MbscResource) =>
        phoneColumnsEnabled.value
          ? getComponentHtml(ScheduleColumnHeader, { resource: data })
          : "",
      renderHeader: () => "",
      // A week needs its seven day headers; a single day does not, because the
      // date row above the grid already names it.
      renderDay: (args: { date: Date }) =>
        isWeekView.value
          ? getComponentHtml(WeekDayHeader, { date: args.date })
          : "",
      onEventClick: handleEventClick,
      onEventRightClick: handleEventRightClick,
      onCellClick: onCellClick,
      onPageLoaded: handlePageLoad,
      ...dragOptionsFor(isSheet.value)
    },
    "mobile",
    // G-TB-2253-1c option (a): startCalendar runs only after flagsResolved
    // is true (the guard above), so mobileWebOptimization.value is safe to
    // read here -- no race between the flag settling and this call.
    mobileWebOptimization.value
  );
};

onMounted(() => {
  if (flagsResolved.value) {
    startCalendar();
  } else {
    const stop = watch(flagsResolved, (resolved) => {
      if (resolved) {
        startCalendar();
        stop();
      }
    });
  }
  // Auto-open appointment details drawer if appointment_id query param is present
  const params = getQueryVariables();
  if (params.appointment_id) {
    selectedAppointmentId.value = params.appointment_id;
  }
});

onBeforeUnmount(() => {
  debouncedDateChange.cancel();
  destroyCalendar();
});

watch(
  () => props.date,
  (newVal) => {
    debouncedDateChange(newVal);
  }
);

watch(isSheet, (sheet) => applyDragGate(sheet));

// The tenant answer can land after the calendar has already painted, and only
// the class below is reactive: Mobiscroll keeps whatever `renderResource`
// returned until the resources are pushed again. Without this the header row is
// revealed empty whenever the roster happens to arrive before the flag.
watch(phoneColumnsEnabled, () => reRenderResources());

const closeEventModal = () => {
  selectedEventId.value = "";
};
const handlePageLoad = () => {
  setTimeout(() => (lastLoad.value = new Date()), 100);
  setTimezoneLabel();
};
const onAppointmentUpdated = (appointment: AppointmentV2) => {
  // Route drawer-driven navigation through the page's selected date so the
  // header, the modal default, and the URL all follow the calendar
  emit("date-navigated", appointment.arrival_window_start);
};
const debouncedDateChange = debounce(
  (date: Date) => setCalendarDate(date),
  750
);
const onHideDrawer = () => (selectedAppointmentId.value = "");

// Guarded here rather than in the composable: whether a tap opens anything is a
// placement decision, and placement belongs to the call site everywhere else in
// this epic. Timeline.vue carries the same guard for the same reason.
const onCellClick = (args: MbscCellClickEvent) => {
  if (!isSheet.value) return;
  handleCellClick(args);
};

// No refresh here: handleSave emits refresh-schedule-board and the composable
// listens for it, so calling refreshTimeline would fetch the board twice.
const onCreated = () => {
  clearCreateSlot();
};
</script>

<style lang="scss">
.mbsc-schedule-all-day-cont {
  display: none;
}

/**
 * G-TB-2253-1c option (a): un-hide the all-day container, but ONLY when it
 * holds an event carrying the phone card's own all-day class
 * (mbl-card--all-day). This stylesheet is injected at import and ships in
 * the desktop bundle regardless of mount (see the comment on the
 * .mbsc-schedule-event:has(.mbl-card) rule below) -- mbl-card--all-day is
 * the whole of its containment, since nothing else in the desktop bundle or
 * the flag-off phone board ever emits that class. Never widen this
 * selector to match on Mobiscroll's own class names alone.
 */
.mbsc-schedule-all-day-cont:has(.mbl-card--all-day) {
  display: block;
}

/*
  Hidden everywhere, as it has always been: this rule is global and the legacy
  and desktop boards both rely on it. Mobiscroll sets no `display` of its own
  here, so without it the band falls back to the UA default and appears on every
  board in the app.

  The phone columns board is the one exception, below.
*/
.mbsc-schedule-header {
  display: none;
}

/*
  Mobiscroll's own date strip, which repeats the day the board's date row above
  it already names -- and that row owns the navigation, so this one is a label
  with nothing to do. Hidden only where that row renders: with the flag off it
  is the board's only date indicator.
*/
.phone-columns .mbsc-schedule-date-header {
  display: none;
}

/*
  The exception to the rule above. renderResource paints each column's header
  into this band, so on the phone columns board it has to stay: hiding it would
  render the headers into a container nobody can see. With one schedule on the
  board the control row above already names it, so it stays hidden there.

  Mobiscroll also leaves the band transparent, so it picks up whatever is behind
  it. White, per the sketch.

  Flex, not block: the band is a flex row -- a spacer the width of the time
  gutter, then the header cells -- so the cells sit over their columns. As
  block the two stacked instead, the cells started at the board's left edge and
  shared the gutter's 68px between them, and every column header sat 68px left
  of its column and a little too wide, on this board and on the week grid alike.
*/
.phone-columns .mbsc-schedule-header {
  display: flex;
  background: #fff;
}

/*
  The header cell's own .5em padding, dropped so the column header owns its
  spacing: it tucks the avatar into the corner and gives the name the column's
  full width, which is what keeps "Unassigned" from truncating.
*/
.phone-columns .mbsc-schedule-resource-title {
  padding: 0;
  /* Fills the cell so the header can centre its content against it rather than
     against its own content box. */
  height: 100%;
}

/*
  Seranova's Avatar hardcodes its clocked-in dot in pixels -- 14px across, at
  `top-0 start-7` (28px in) -- all measured against the 40px avatar it draws
  everywhere else. On the 20px avatar a column header can afford, those same
  pixels put the dot clear of the avatar and on top of the first name.

  Restated as the ratios the 40px avatar already uses, so the dot lands in the
  same place on the small avatar as on the large one and follows any future size
  change: 14/40 = 35% for the size, 28/40 = 70% for the inset. Top-right corner,
  the same corner as everywhere else.

  `!important` is not a preference: this project's Tailwind emits utilities as
  important, so the component's own `w-3.5`/`top-0`/`start-7` cannot be beaten
  without it.
*/
.phone-columns .clocked-in-status-icon {
  top: 0 !important;
  inset-inline-start: 70% !important;
  inset-inline-end: auto !important;
  width: 35% !important;
  height: 35% !important;
  border-width: 1px !important;
}

/*
  A hairline between columns, running the full height of the grid so a card can
  be read against the schedule it belongs to. Adjacent-sibling so the first
  column carries no leading rule.
*/
.phone-columns .mbsc-schedule-resource + .mbsc-schedule-resource,
.phone-columns .mbsc-schedule-column + .mbsc-schedule-column {
  border-left: 1px solid #e5e7eb;
}
/*
  Today's column, tinted behind the cards. Mobiscroll marks its HEADER with
  `mbsc-selected`, but that follows the date the board is focused on rather than
  today, and the columns themselves carry no today class at all -- so the
  weekday suffix is the handle, switched on by the wrapper's data attribute.
*/
$weekdays: mon, tue, wed, thu, fri, sat, sun;
@each $day in $weekdays {
  .phone-week-view[data-today-column="#{$day}"] .mbsc-schedule-column-#{$day} {
    background-color: #eff6ff;
  }
}

/*
  The now line spans the whole week and fades. Mobiscroll's own dot on the day
  segment -- its :before, at today's column's left edge, which is where the
  sketch puts it -- marks today at full strength. The fade is done through the
  line's colour rather than opacity, which would take the dot with it if the
  segment sits inside the line's element.
*/
.phone-week-view .mbsc-schedule-time-indicator-x {
  border-bottom-color: rgba(37, 99, 235, 0.35);
}
.phone-week-view .mbsc-schedule-time-indicator-day::before {
  background-color: #2563eb;
}

/*
  The time pill Mobiscroll draws at the line's left end reads "6:00 AM (CDT)",
  twice the width of the week gutter below, so it lay across the first column's
  cards. The line, the dot and the header's blue disc already say when and where
  "now" is; the day board keeps the pill.
*/
.phone-week-view .mbsc-schedule-time-indicator-time-x {
  display: none;
}

/*
  Mobiscroll divides a week column between whatever runs concurrently and
  reserves room for its own "+N" chip, which on a 360px phone left a card 9px of
  a 40px column -- a colour with no room for a title. Its computed `left` is the
  offset the design asks for and is kept; only the width is taken back, so a
  card alone in its column fills it and overlapping ones ride on top with the
  earlier card's stripe still showing at the left edge.
*/
/*
  Mobiscroll divides a week column between whatever runs concurrently and
  reserves room for its own "+N" chip, which on a 360px phone left a card 9px of
  a 40px column -- a colour with no room for a title. Its computed `left` is the
  offset the design asks for and is kept; only the width is taken back, so a
  card alone in its column fills it and overlapping ones ride on top with the
  earlier card's stripe still showing at the left edge.

  Timed events only: `.mbsc-schedule-column` excludes the all-day track, which
  Mobiscroll lays out by its own rules.
*/
.phone-week-view .mbsc-schedule-column .mbsc-schedule-event {
  width: 92% !important;
}

/*
  The time gutter, narrowed from Mobiscroll's 4.25em (68px) to 40px: a fifth of
  a 360px phone was going to "12 AM" at 10px, and every pixel here is one the
  seven columns share. At 10px the widest label ("12 PM") is about 30px, so
  with the padding brought in from .8em a side to 4px every label still fits on
  one line; a wider device font would wrap it, which the default white-space
  allows, rather than spill it onto Monday's cards. Three things move together
  because all three are sized from that same 4.25em in Mobiscroll's stylesheet:
  the column, the label wrapper inside it, and the now line's left edge; leave
  one behind and either the labels overflow the gutter or the line starts 28px
  into the grid.
*/
.phone-week-view .mbsc-schedule-time-col,
.phone-week-view .mbsc-schedule-time-wrapper {
  width: 40px;
}
.phone-week-view .mbsc-schedule-time {
  padding: 0 4px;
  text-align: right;
}
.phone-week-view .mbsc-schedule-time-indicator-x.mbsc-ltr {
  left: 40px;
}
.phone-week-view .mbsc-schedule-all-day-text {
  font-size: 0.625em;
  padding: 0.5em 0.3em;
}

/*
  An edge, which the card does not draw for itself. On the day board two
  overlapping cards are told apart by the offset Mobiscroll gives them --
  roughly a third of a 287px column. At week width that same offset is 9px,
  so two cards of the same status (a column of unassigned work is all one red)
  fused into a single block with nothing to say where one ended.

  The border is on the SEGMENTS, not on the Mobiscroll box around them. The
  box is the appointment's time footprint, and the card widens it to the
  arrival window on purpose; the segments are what is painted. A running job
  paints nothing between the window opening and the tech accepting, so a
  border on the box outlined that empty band -- and every patch it then needed
  (the box's own padding, its radius against the segment's, the all-day boxes
  that hold no card) was a consequence of outlining the footprint rather than
  the paint.

  Composed the way the card already composes its corners: sides on every
  segment, top on `first`, bottom on `last`. The segments all span left:0 to
  right:0, so the sides line up into one continuous outline around whatever is
  painted, and each border follows its own segment's radius, so there is no
  inner-curve arithmetic to get wrong. Nothing here reaches a calendar event or
  a hold, which render no segments and draw their own borders.

  The grey is PHONE_MARKER_COLORS.holdBorder / eventBorder from
  useScheduleColors -- the grey this card already outlines its hold and
  calendar-event markers in.
*/
.phone-week-view .mbl-card-segment {
  border-left: 1px solid #9aa4ab;
  border-right: 1px solid #9aa4ab;
}
.phone-week-view .mbl-card-segment--rounded-top {
  border-top: 1px solid #9aa4ab;
}
.phone-week-view .mbl-card-segment--rounded-bottom {
  border-bottom: 1px solid #9aa4ab;
}

.mbsc-schedule-time-indicator-time.mbsc-schedule-time-indicator-time-y {
  background-color: #2563eb; /* A nice shade of blue */
  color: white; /* Sets the text color to white */
  padding: 2px 6px; /* Adds some vertical and horizontal spacing */
  border-radius: 4px; /* Rounds the corners of the container */
}
/*
  This targets the pseudo-element that creates the horizontal line
  and centers it within its container.
*/
.mbsc-schedule-time-indicator-y::after {
  left: 50% !important;
  transform: translateX(-50%) !important;
}

/*
  This re-centers the time label directly above the
  now-centered line.
*/
.mbsc-schedule-time-indicator-time-y {
  left: 50% !important;
  transform: translateX(-42%) !important;
}

/*
  Two unstyled wrapper divs sit between .mbsc-schedule-event and .mbl-card,
  confirmed against the live DOM during this task's Playwright run: Mobiscroll
  inserts its OWN render wrapper as the direct child of .mbsc-schedule-event,
  and getComponentHtml returns container.outerHTML -- the container div it
  mounted the app into -- which becomes that wrapper's own child, with
  .mbl-card a child of THAT. .mbl-card's own h-full then resolves against the
  innermost wrapper's auto height, and the card's container-type: size turns
  an auto height into zero -- a card that exists in the DOM and occupies no
  pixels. Both intermediate wrappers need an explicit height for the chain to
  reach .mbl-card at all.

  This block is NOT phone-scoped by construction: an SFC's <style> block is
  injected when the module is imported, and SchedulePageV2.vue imports this
  file statically, so this un-scoped block (like the four !important rules
  above it) ships in the desktop bundle too. What keeps it inert on the
  desktop board is that the selector is keyed on .mbl-card, a class only the
  new phone card ever emits -- never widen this to a bare descendant
  selector such as ".mbsc-schedule-event > div", which would also match the
  desktop timeline's own event wrapper.

  BROWSER FLOOR, on purpose and unmeasured: every phone card's box depends on
  `:has()`, as does the all-day container rule further up. A browser without it
  (Safari below 15.4, Chrome/Edge below 105, Firefox below 121) does not
  degrade -- the height chain never reaches .mbl-card, container-type: size
  resolves the auto height to zero, and the board renders cards that occupy no
  pixels. A blank board, not a cosmetic difference.

  This was accepted rather than measured: nobody has checked these selectors
  against the supported-browser floor. If a report arrives describing an empty
  phone scheduler on an older device, start here -- the fix is an explicit
  height on the two intermediate wrappers, keyed on something other than
  `:has()`, not a tweak to the card itself.
*/
.mbsc-schedule-event:has(.mbl-card) > div {
  height: 100%;

  > div {
    height: 100%;
  }
}

/**
 * G-TB-2253-1c option (a), Task 3: the D-08 !important width/left widening
 * rule this comment block used to carry is RETIRED here, by measurement,
 * not assumption. It existed to force an all-day event's card back to full
 * width after Mobiscroll's TIMED-lane overlap stacking narrowed it to
 * share space with a sibling event -- the exact bug WR-05 reported. Once
 * Task 2 routed every all-day event into Mobiscroll's own all-day track
 * (event_details.all_day marks the TimelineEvent, never rendered in
 * .mbsc-schedule-events), that lane-narrowing computation never runs
 * against it, so the rule had nothing left to fight. Confirmed live with
 * TWO same-day all-day events on one technician: Mobiscroll stacks them as
 * separate full-width ROWS in the all-day track (each inline
 * style="width: 100%"), never narrowed side-by-side the way two timed
 * events would be -- the same "acceptable, not fixed" outcome the plan
 * names for that edge case. Keeping the !important override would have
 * been harmless for one event but would have forced every all-day card in
 * a shared row to width: 100% regardless of how many rows Mobiscroll
 * stacked, which is not something this plan needs and could mask a real
 * future stacking change. If a future gap reintroduces lane-based
 * narrowing, re-derive the fix from the live DOM again rather than
 * restoring this rule from history.
 */

/**
 * G-TB-2253-1c option (a): Mobiscroll's own all-day item
 * (.mbsc-schedule-event-all-day) sets min-height: 0 and no height -- the
 * nested-wrapper chain above (.mbsc-schedule-event:has(.mbl-card) > div)
 * resolves its own height: 100% against that undefined height and
 * collapses to zero, confirmed against the live DOM this task: the card
 * mounted with a 4px bounding box and its title rendered clipped behind
 * the day header. The same failure plan 01 found for the timed lane
 * (an auto height flowing into a container-type: size card). No
 * !important needed -- nothing else sets a height on this element, only
 * min-height: 0. Keyed on mbl-card--all-day, a class only the new phone
 * card emits when event_details.all_day is true (D-08: no all-technician
 * flag exists or may be added) -- the same containment reasoning as the
 * .mbsc-schedule-event:has(.mbl-card) > div rule above, and what keeps
 * this rule off the desktop board and inert when the flag is off.
 */
.mbsc-schedule-event:has(.mbl-card--all-day) {
  height: 1.875em;
}
</style>
